import React from "react";

const RELOAD_KEY = "vornx_chunk_reloaded";

const isChunkError = (error) => {
  const msg = (error && (error.message || String(error))) || "";
  return (
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
};

// Global safety net. Its main job is to recover gracefully when a code-split
// chunk fails to load after a new Deploy (stale build in an open tab):
// it performs a one-time controlled reload to fetch the fresh build.
// For any other render error it shows a branded fallback with a manual reload.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    if (isChunkError(error)) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
      } catch (_) {}
      if (!alreadyReloaded) {
        try {
          sessionStorage.setItem(RELOAD_KEY, "1");
        } catch (_) {}
        this.setState({ reloading: true });
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch (_) {}
    window.location.reload();
  };

  render() {
    if (this.state.reloading) {
      // Auto-reload in flight to recover the fresh build.
      return <div className="min-h-screen bg-ink" data-testid="error-boundary-reloading" />;
    }
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary-fallback" className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-gold">VORNX</span>
          <h1 className="mt-6 font-display text-3xl font-light tracking-tight text-gold sm:text-4xl">
            Si è verificato un problema
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
            Ricarica la pagina per continuare. Se il problema persiste, riprova tra qualche istante.
          </p>
          <button
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            className="mt-8 bg-gold px-8 py-4 text-xs uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-gold-muted"
          >
            Ricarica la pagina
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
